import type { ProcessingRun } from './api'

export const processingOutcomeLabels: Record<ProcessingRun['outcome'], string> = {
  verified: 'Đã xác thực',
  needs_review: 'Cần nhân viên kiểm tra',
  needs_input: 'Cần ảnh khác',
  no_match: 'Chưa tìm thấy đơn',
  failed: 'Xử lý gián đoạn',
  duplicate: 'Đã gửi trước đó',
}

export const registrationStatusLabels: Record<string, string> = {
  pending: 'Đang chờ xử lý',
  processing: 'Đang xử lý',
  verified: 'Đã xác thực',
  needs_review: 'Cần nhân viên kiểm tra',
  no_match: 'Chưa tìm thấy đơn',
  rejected: 'Kết quả không còn hiệu lực',
}

export const matchMethodLabels: Record<string, string> = {
  exact_platform_order_id_dwh: 'Khớp chính xác mã đơn sàn trong DWH',
  exact_order_id_plus_product_dwh: 'Khớp mã đơn và sản phẩm trong DWH',
  exact_order_id_product_mismatch_dwh: 'Khớp mã đơn; sản phẩm cần kiểm tra',
  ambiguous_platform: 'Mã đơn xuất hiện ở nhiều kênh',
}

export const processingStageLabels: Record<string, string> = {
  check: 'Kiểm tra yêu cầu trước đó',
  upload: 'Tiếp nhận ảnh',
  ocr: 'Đọc ảnh OCR',
  verify: 'Đối chiếu và lưu',
  complete: 'Kết thúc xử lý',
  error: 'Xử lý gián đoạn',
}

function digitsOnly(value: unknown) {
  return String(value || '').replace(/\D/g, '')
}

function normalizePhone(value: unknown) {
  let digits = digitsOnly(value)
  if (digits.startsWith('84') && digits.length >= 11) digits = `0${digits.slice(2)}`
  return digits
}

export function maskedPhoneEvidenceMatches(customerPhone: unknown, imagePhone: unknown): boolean | null {
  const phone = normalizePhone(customerPhone)
  const evidence = String(imagePhone || '').trim()
  if (!phone || !evidence) return null

  const firstMask = evidence.search(/[xX*•]/)
  if (firstMask < 0) {
    const visiblePhone = normalizePhone(evidence)
    return visiblePhone ? visiblePhone === phone : null
  }

  let prefix = digitsOnly(evidence.slice(0, firstMask))
  const lastMask = Math.max(evidence.lastIndexOf('*'), evidence.lastIndexOf('x'), evidence.lastIndexOf('X'), evidence.lastIndexOf('•'))
  const suffix = digitsOnly(evidence.slice(lastMask + 1))
  if (prefix.startsWith('84')) prefix = `0${prefix.slice(2)}`
  if (!prefix && !suffix) return null
  return (!prefix || phone.startsWith(prefix)) && (!suffix || phone.endsWith(suffix))
}

export function adminReasonTitle(reason: string) {
  if (reason === 'customer_phone_conflicts_with_order') return 'Số đầy đủ chưa khớp dữ liệu đơn'
  if (reason === 'customer_phone_conflicts_with_image') return 'Phần số trên ảnh chưa khớp'
  if (reason === 'customer_phone_missing_in_order') return 'Đơn thiếu số để đối chiếu'
  if (reason === 'superseded_lookup_policy') return 'Kết quả cũ không còn hiệu lực'
  return 'Cần nhân viên kiểm tra'
}

export function adminReasonMessage(reason: string, customerPhone?: unknown, imagePhone?: unknown) {
  if (reason === 'customer_phone_conflicts_with_order') {
    const imageMatches = maskedPhoneEvidenceMatches(customerPhone, imagePhone)
    if (imageMatches === true) return 'Phần số hiển thị trên ảnh khớp với số khách cung cấp, nhưng số nhận hàng đầy đủ trong DWH không khớp. Cần kiểm tra các chữ số đang bị che.'
    if (imageMatches === false) return 'Số khách cung cấp không khớp số nhận hàng đầy đủ trong DWH; phần đầu hoặc đuôi hiển thị trên ảnh cũng không khớp.'
    return 'Số khách cung cấp không khớp số nhận hàng đầy đủ trong DWH. Ảnh không có đủ phần số để xác nhận toàn bộ.'
  }

  const messages: Record<string, string> = {
    customer_phone_conflicts_with_image: 'Số khách cung cấp không khớp phần đầu hoặc đuôi đang hiển thị trên ảnh.',
    customer_phone_missing_or_invalid: 'Số khách cung cấp thiếu hoặc sai định dạng nên chưa thể đối chiếu.',
    customer_phone_missing_in_order: 'Đơn hàng trong DWH không có số nhận hàng để hệ thống tự động đối chiếu.',
    customer_identity_incomplete: 'Ảnh không đọc được cả tên lẫn địa chỉ người nhận để bổ sung bằng chứng danh tính.',
    order_already_verified: 'Đơn hàng đã có lượt xác thực trước đó; số khách vừa cung cấp chưa được tự động xác nhận.',
    submission_phone_conflicts_existing: 'Cùng ảnh hoặc đơn hàng đã có lượt xác thực bằng số khác; số vừa cung cấp chưa được xác nhận.',
    platform_order_not_found: 'Chưa tìm thấy đúng mã đơn sàn trong DWH theo phạm vi tra cứu hiện hành (tối đa 36 tháng).',
    superseded_lookup_policy: 'Kết quả cũ đã bị vô hiệu vì chính sách tra cứu thay đổi; không dùng kết quả này để kết luận hiện tại.',
    platform_required: 'Mã đơn có thể thuộc nhiều kênh; cần xác định đúng Shopee hoặc TikTok Shop.',
  }
  return messages[reason] || 'Trường hợp này chưa có mô tả vận hành. Cần kiểm tra nhật ký các chặng.'
}

export function displayProcessingMessage(message: unknown) {
  const text = String(message || '').trim()
  if (!text) return '—'
  if (/số điện thoại khách (?:nhập|cung cấp) không khớp thông tin nhận hàng của đơn/i.test(text)) {
    return 'Số khách cung cấp chưa khớp số nhận hàng đầy đủ trong DWH; cần nhân viên kiểm tra.'
  }
  if (/Shopee\/TikTok Shop (?:30 ngày|6 tháng) gần đây/i.test(text)) {
    return 'Chưa tìm thấy mã đơn theo phạm vi tra cứu tại thời điểm xử lý. Đây là thông báo theo chính sách cũ; phạm vi hiện tại tối đa 36 tháng.'
  }
  return text
}
